import { useFormik } from 'formik';
import { useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';

import { useMutation } from '@apollo/client';
import { useMounted } from '../../hooks/use-mounted';
import { useResponsive } from '../../hooks/use-responsive';
import Button from '../basicComponents/button';
import Input from '../basicComponents/input';
import { SEND_RESET_PASSWORD_EMAIL_MUTATION } from '../settings-page/users/queries';
import { StyledLink } from './login';

const ResetPassword = () => {
  const isMounted = useMounted();
  const { isMobile, isTablet, isPortrait } = useResponsive();

  const [isRequestedReset, setIsRequestedReset] = useState<boolean>(false);

  const [sendResetPasswordEmail] = useMutation(SEND_RESET_PASSWORD_EMAIL_MUTATION);

  const formik = useFormik({
    initialValues: {
      email: '',
      submit: null,
      isSubmitting: true
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Must be a valid email').max(255).required('Email is required')
    }),
    onSubmit: async (values, helpers): Promise<void> => {
      try {
        await sendResetPasswordEmail({
          variables: {
            email: values.email
          }
        });

        setIsRequestedReset(true);
      } catch (err: any) {
        console.error(err);
        if (isMounted()) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }
    }
  });

  return (
    <ResetPasswordWrapper isMobile={isMobile} isTablet={isTablet} isPortrait={isPortrait}>
      {isRequestedReset ? (
        <div style={{ width: '100%', textAlign: 'center' }}>
          If your email address is registered you will receive a password reset email in a few minutes.
        </div>
      ) : (
        <>
          <p
            style={{
              paddingBottom: '44px',
              color: 'passwordLabelText',
              fontWeight: 500,
              fontSize: 16,
              width: '100%',
              textAlign: 'center'
            }}
          >
            Please enter your email address to receive a reset password.
          </p>
          <form noValidate onSubmit={formik.handleSubmit}>
            <Input
              label="Enter Your Email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              styleType={formik.touched.email && formik.errors.email ? 'error' : 'success'}
              errorText={formik.touched.email && formik.errors.email}
              type="email"
              name="email"
              size="lg"
              isRequiredField
            />
            <RequestResetButtonWrapper>
              <Button
                id="sign-in-button"
                styleType={formik.isSubmitting || !formik.isValid ? 'disabled' : 'default'}
                type="submit"
                size="lg"
                width="100%"
              >
                Request Reset
              </Button>
            </RequestResetButtonWrapper>
          </form>
        </>
      )}
      <BackToWrapper>
        <StyledLink to="/">Back to log in</StyledLink>
      </BackToWrapper>
    </ResetPasswordWrapper>
  );
};

export default ResetPassword;

const ResetPasswordWrapper = styled.div<{ isMobile: boolean; isPortrait: boolean; isTablet: boolean }>`
  width: ${({ isMobile, isPortrait, isTablet }) => (isMobile || (isPortrait && isTablet) ? '70%' : '100%')};
  font-size: 19px;
`;

const BackToWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const RequestResetButtonWrapper = styled.div`
  margin-top: 30px;
`;
